import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Icon from './Icon';
import { CASE_STUDIES, buildDetail, findCaseBySlug } from './data';
import { useRegisterLocaleHref } from '@/contexts/LocaleHrefContext';
import useTrans from '../../hooks/useTrans';

const SUPPORTED_LOCALES = ['vi', 'en', 'jp'];

const isVideoUrl = (url) =>
  typeof url === 'string' && /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i.test(url);

// Touch-swipe handlers for carousels: a horizontal drag of 40px+ triggers
// prev/next. We bind to the viewport (not the track) so the touch surface
// stays put while the track translates.
function useSwipe(onPrev, onNext) {
  const ref = useRef({ x: 0, y: 0, active: false });
  return {
    onTouchStart: (e) => {
      const t = e.touches[0];
      ref.current = { x: t.clientX, y: t.clientY, active: true };
    },
    onTouchMove: (e) => {
      if (!ref.current.active) return;
      const t = e.touches[0];
      // If the gesture is clearly horizontal, prevent the browser from
      // hijacking it as a vertical scroll.
      if (Math.abs(t.clientX - ref.current.x) > Math.abs(t.clientY - ref.current.y)) {
        // No-op: cannot preventDefault on passive listeners, but the early
        // return below handles the cancellation case if needed.
      }
    },
    onTouchEnd: (e) => {
      if (!ref.current.active) return;
      ref.current.active = false;
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      const dx = t.clientX - ref.current.x;
      const dy = t.clientY - ref.current.y;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) onNext(); else onPrev();
    },
  };
}

// Match the CSS breakpoints (1100/700) used for case-studies layout so the
// gallery/applications carousels show a usable number of slides per viewport.
function useVisibleCount(desktop = 4) {
  const [count, setCount] = useState(desktop);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const update = () => {
      const w = window.innerWidth;
      if (w <= 700) setCount(1);
      else if (w <= 1100) setCount(2);
      else setCount(desktop);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [desktop]);
  return count;
}

function DetailHero({ c, d, t }) {
  const defaultIcons = ['shield-down', 'shield-check', 'clock', 'check-circle'];
  const stats = (d && Array.isArray(d.benefits) ? d.benefits : []).slice(0, 3);
  return (
    <section className='ef-d-hero'>
      <div className='ef-container ef-d-hero__wrap'>
        <div className='ef-d-hero__card'>
          {c.image && <img className='ef-d-hero__bg' src={c.image} alt='' />}
          <div className='ef-d-hero__copy-panel'>
            <div className='ef-d-hero__brand'>
              <span className='ef-d-hero__badge'>{t.badge}</span>
              <span className='ef-d-hero__divider' />
              <span className='ef-d-hero__industry'>{c.industry}</span>
            </div>
            <h1>{(c.title || '').trim()}</h1>
            <p className='lede'>{c.excerpt}</p>
            {stats.length > 0 && (
              <div className='ef-d-hero__stats'>
                {stats.map((b, i) => {
                  const icon = b.icon || defaultIcons[i] || 'shield';
                  return (
                    <div className='ef-d-hero__stat' key={i}>
                      <span className='ef-d-hero__stat-icn'>
                        <Icon name={icon} size={20} stroke={1.8} />
                      </span>
                      <div className='ef-d-hero__stat-body'>
                        <div className='ef-d-hero__stat-v'>
                          {b.v}<span>{b.unit}</span>
                        </div>
                        {b.desc && <div className='ef-d-hero__stat-k'>{b.desc}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className='ef-d-hero__ctas'>
              <Link href='/contact'>
                <a className='ef-btn ef-btn--primary ef-btn--lg ef-d-hero__cta-primary'>
                  {t.hero.ctaPrimary}
                </a>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Helper: prefer the array when it has content, otherwise the fallback.
// `(arr || fallback)` is wrong because an empty [] is truthy in JS.
const arrOr = (arr, fallback) =>
  Array.isArray(arr) && arr.length > 0 ? arr : fallback;

// Split arbitrary CMS text into paragraphs. Accepts either a string or an
// array of strings — handles legacy payloads where multi-line text was
// stored as a single string with embedded "\n", and new payloads where
// each line is already its own array entry.
const toParagraphs = (input) => {
  const lines = Array.isArray(input) ? input : [input];
  return lines
    .filter((s) => typeof s === 'string')
    .flatMap((s) => s.split(/\r?\n/))
    .map((s) => s.trim())
    .filter(Boolean);
};

function ThumbnailSlider({ images, captions, visibleCount, gap = 12 }) {
  const total = images.length;
  const VISIBLE = Math.max(1, Math.min(visibleCount, total));
  const maxStart = Math.max(0, total - VISIBLE);
  const [start, setStart] = useState(0);
  useEffect(() => {
    setStart((s) => Math.min(s, maxStart));
  }, [maxStart]);
  const prev = () => setStart((s) => Math.max(0, s - 1));
  const next = () => setStart((s) => Math.min(maxStart, s + 1));
  const swipe = useSwipe(prev, next);
  if (total === 0) return null;
  const overflow = total > VISIBLE;
  if (!overflow) {
    return (
      <div className='ef-d-thumbs ef-d-thumbs--grid' style={{ '--col-count': VISIBLE }}>
        {images.map((src, i) => (
          <figure key={i}>
            <img src={src} alt='' />
            {captions[i] && <figcaption>{captions[i]}</figcaption>}
          </figure>
        ))}
      </div>
    );
  }
  const trackStyle = {
    gridAutoColumns: `calc((100% - ${gap}px * ${VISIBLE - 1}) / ${VISIBLE})`,
    transform: `translateX(calc(-${start} * (100% / ${VISIBLE} + ${gap}px / ${VISIBLE})))`,
    gap: `${gap}px`,
  };
  return (
    <div className='ef-d-thumbs'>
      <button
        className='ef-d-thumbs__nav prev'
        aria-label='Previous'
        type='button'
        onClick={prev}
        disabled={start === 0}
      >
        <Icon name='back' size={16} />
      </button>
      <div className='ef-d-thumbs__viewport' {...swipe}>
        <div className='ef-d-thumbs__track' style={trackStyle}>
          {images.map((src, i) => (
            <figure key={i}>
              <img src={src} alt='' />
              {captions[i] && <figcaption>{captions[i]}</figcaption>}
            </figure>
          ))}
        </div>
      </div>
      <button
        className='ef-d-thumbs__nav next'
        aria-label='Next'
        type='button'
        onClick={next}
        disabled={start === maxStart}
      >
        <Icon name='arrow-right' size={16} />
      </button>
    </div>
  );
}

function OverviewGallery({ images, captions }) {
  const visible = useVisibleCount(3);
  return (
    <ThumbnailSlider
      images={images}
      captions={captions}
      visibleCount={visible}
      gap={12}
    />
  );
}

function ChallengeMedia({ images, captions }) {
  const visible = useVisibleCount(3);
  return (
    <ThumbnailSlider
      images={images}
      captions={captions}
      visibleCount={visible}
      gap={12}
    />
  );
}

function SolutionGallery({ images, captions }) {
  const visible = useVisibleCount(3);
  return (
    <ThumbnailSlider
      images={images}
      captions={captions}
      visibleCount={visible}
      gap={12}
    />
  );
}

function OverviewChallenge({ c, d, t }) {
  // Prefer overview.images/captions (curated by editor for this section);
  // fall back to gallery; finally fall back to the thumbnail.
  const galleryImgs = arrOr(
    d.overview && d.overview.images,
    arrOr(d.real && d.real.images, [c.image])
  );
  const galleryCaps = arrOr(
    d.overview && d.overview.captions,
    arrOr(d.real && d.real.captions, [])
  );
  const overviewBody = toParagraphs(d.overview && d.overview.sub);
  const challengeImages = (() => {
    const c = d.challenge || {};
    if (Array.isArray(c.images) && c.images.length) return c.images.filter(Boolean);
    if (typeof c.media === 'string' && c.media) return [c.media];
    return [];
  })();
  const challengeCaptions = (() => {
    const c = d.challenge || {};
    if (Array.isArray(c.captions)) return c.captions;
    if (typeof c.mediaCaption === 'string') return [c.mediaCaption];
    return [];
  })();
  const challengeBlocks = (() => {
    const c = d.challenge || {};
    if (Array.isArray(c.blocks) && c.blocks.length) {
      return c.blocks
        .map((b) => {
          if (!b || typeof b !== 'object') return null;
          if (b.type === 'paragraph') {
            const text = typeof b.text === 'string' ? b.text.trim() : '';
            return text ? { type: 'paragraph', text } : null;
          }
          if (b.type === 'bullets') {
            const items = Array.isArray(b.items)
              ? b.items.filter((it) => typeof it === 'string' && it.trim() !== '')
              : [];
            return items.length ? { type: 'bullets', items } : null;
          }
          return null;
        })
        .filter(Boolean);
    }
    const legacyBody = toParagraphs(c.body);
    const legacyBullets = arrOr(c.bullets, []).filter(
      (it) => typeof it === 'string' && it.trim() !== ''
    );
    const fallback = legacyBody.map((text) => ({ type: 'paragraph', text }));
    if (legacyBullets.length) fallback.push({ type: 'bullets', items: legacyBullets });
    return fallback;
  })();

  return (
    <section className='ef-d-section'>
      <div className='ef-container'>
        <h2 className='ef-d-h'><span className='n'></span> {t.section1.h}</h2>
        <div className='ef-d-overview'>
          <div className='ef-d-overview__left'>
            <OverviewGallery images={galleryImgs} captions={galleryCaps} />
            {overviewBody.map((p, i) => <p key={i} className='ef-d-overview__text'>{p}</p>)}
          </div>
          <aside className='ef-d-challenge'>
            <h3>{t.section1.challengeH}</h3>
            {challengeImages.length > 0 && (
              <ChallengeMedia
                images={challengeImages}
                captions={challengeCaptions}
              />
            )}
            {challengeBlocks.length > 0 ? (
              challengeBlocks.map((block, i) => {
                if (block.type === 'paragraph') {
                  return (
                    <p key={i} className='ef-d-challenge__intro'>{block.text}</p>
                  );
                }
                return (
                  <ul key={i} className='ef-d-challenge__list'>
                    {block.items.map((b, j) => (
                      <li key={j}>
                        <span className='bullet-dot' />
                        <span>
                          <strong>{b.split(':')[0] || b}</strong>
                          {b.includes(':') && <>: {b.split(':').slice(1).join(':').trim()}</>}
                        </span>
                      </li>
                    ))}
                  </ul>
                );
              })
            ) : (
              t.section1.challengeIntro && (
                <p className='ef-d-challenge__intro'>{t.section1.challengeIntro}</p>
              )
            )}
          </aside>
        </div>
      </div>
    </section>
  );
}

function SolutionModel({ d, t }) {
  const intro = toParagraphs(d.solution && d.solution.intro);
  const modelImage = (d.solution && d.solution.modelImage) || '';
  const highlights = arrOr(d.solution && d.solution.highlights, []);
  const solutionImages = (() => {
    const s = d.solution || {};
    return Array.isArray(s.images) ? s.images.filter(Boolean) : [];
  })();
  const solutionCaptions = (() => {
    const s = d.solution || {};
    return Array.isArray(s.captions) ? s.captions : [];
  })();

  const steps = d.howItWorks || [];
  const n = steps.length;

  return (
    <section className='ef-d-section'>
      <div className='ef-container'>
        <h2 className='ef-d-h'><span className='n'></span> {t.section2.h}</h2>
        <div className='ef-d-solution'>
          <div className='ef-d-solution__copy'>
            {intro.map((p, i) => <p key={i}>{p}</p>)}
            {highlights.length > 0 && (
              <div className='ef-d-highlights'>
                <h4>{t.section2.highlightsH}</h4>
                <ul>
                  {highlights.map((h, i) => (
                    <li key={i}>
                      <span className='check'><Icon name='check' size={12} stroke={3} /></span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {solutionImages.length > 0 && (
              <div className='ef-d-solution__gallery'>
                <SolutionGallery
                  images={solutionImages}
                  captions={solutionCaptions}
                />
              </div>
            )}
          </div>
          <div className='ef-d-model'>
            <h3 className='ef-d-model__h'>{t.section2.modelH}</h3>
            {modelImage ? (
              <div className='ef-d-model__img'>
                <img
                  src={modelImage}
                  alt={t.section2.modelH}
                  style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 12 }}
                />
              </div>
            ) : (
              <div
                className='ef-d-model__img'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 280,
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 12,
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: 14,
                  padding: 24,
                  textAlign: 'center',
                }}
              >
                {t.section2.modelPlaceholderLine1}
                <br />
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  {t.section2.modelPlaceholderLine2}
                </span>
              </div>
            )}
          </div>
        </div>

        {n > 0 && (
          <div className='ef-d-how'>
            <h3 className='ef-d-how__h'>
              {t.section2.howH}
              {t.section2.howAlt && (
                <> <span className='light'>{t.section2.howAlt}</span></>
              )}
            </h3>
            <div
              className='ef-d-how__timeline'
              data-count={n}
              style={{ gridTemplateColumns: `repeat(${n}, 1fr)` }}
            >
              <div className='ef-d-how__line' />
              {steps.map((s, i) => (
                <div className='ef-d-how__step' key={i}>
                  <div className='ef-d-how__num'>
                    <span>{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <h4>{s.t}</h4>
                  <p>{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
}

function Lightbox({ items, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  const it = items[index];
  if (!it) return null;
  return (
    <div className='ef-d-lightbox' onClick={onClose}>
      <button className='ef-d-lightbox__close' onClick={onClose} aria-label='Close' type='button'>×</button>
      <button
        className='ef-d-lightbox__nav prev'
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label='Previous'
        type='button'
      >
        <Icon name='back' size={26} />
      </button>
      <div className='ef-d-lightbox__stage' onClick={(e) => e.stopPropagation()}>
        {isVideoUrl(it.src) ? (
          <video src={it.src} controls autoPlay playsInline />
        ) : (
          <img src={it.src} alt='' />
        )}
        {it.cap && (
          <p className='ef-d-lightbox__cap'>
            {typeof it.cap === 'string' ? it.cap : (it.cap.split ? it.cap.split('\n').join(' — ') : it.cap)}
          </p>
        )}
        <span className='ef-d-lightbox__count'>{index + 1} / {items.length}</span>
      </div>
      <button
        className='ef-d-lightbox__nav next'
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label='Next'
        type='button'
      >
        <Icon name='arrow-right' size={26} />
      </button>
    </div>
  );
}

function Results({ d, t }) {
  const benefits = (d.benefits || []).slice(0, 4);
  const defaultIcons = ['shield-down', 'shield-check', 'clock', 'check-circle'];
  const defaultAccents = ['red', 'orange', 'blue', 'green'];
  const outcomes = arrOr(d.outcomes, []);

  return (
    <div className='ef-d-col'>
      <h2 className='ef-d-h'><span className='n'></span> {t.section3.h}</h2>
      <div className='ef-d-stats'>
        {benefits.map((b, i) => {
          const icon = b.icon || defaultIcons[i] || 'shield';
          const accent = b.accent || defaultAccents[i] || 'blue';
          return (
          <div className={`ef-d-stat accent--${accent}`} key={i}>
            <div className='ef-d-stat__head'>
              <div className='ef-d-stat__icn'><Icon name={icon} size={18} /></div>
              <div className='ef-d-stat__v'>
                {b.v}<span>{b.unit}</span>
              </div>
            </div>
            <p>{b.desc}</p>
          </div>
          );
        })}
      </div>
      {outcomes.length > 0 && (
        <div className='ef-d-outcomes'>
          <h4>{t.section3.outcomesH}</h4>
          <ul>
            {outcomes.map((o, i) => (
              <li key={i}>
                <span className='bullet-dot' />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RealImages({ d, t }) {
  const images = (d.real && d.real.images) || [];
  const captions = (d.real && d.real.captions) || [];
  const all = images.map((src, i) => ({
    src,
    cap: captions[i] || '',
  }));

  const VISIBLE = useVisibleCount();
  const maxStart = Math.max(0, all.length - VISIBLE);
  const [start, setStart] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  // Clamp start when VISIBLE drops on resize so we don't park past the end.
  useEffect(() => {
    setStart((s) => Math.min(s, maxStart));
  }, [maxStart]);

  if (!all.length) return null;

  const prev = () => setStart((s) => Math.max(0, s - 1));
  const next = () => setStart((s) => Math.min(maxStart, s + 1));
  const overflow = all.length > VISIBLE;
  const dots = maxStart + 1;
  const swipe = useSwipe(prev, next);
  const trackStyle = {
    gridAutoColumns: `calc((100% - 8px * ${VISIBLE - 1}) / ${VISIBLE})`,
    transform: `translateX(calc(-${start} * (100% / ${VISIBLE} + 8px / ${VISIBLE})))`,
  };

  return (
    <div className='ef-d-col'>
      <h2 className='ef-d-h'><span className='n'></span> {t.section4.h}</h2>
      <div className='ef-d-gallery'>
        {overflow && (
          <button
            className='ef-d-gallery__nav prev'
            aria-label='Previous'
            type='button'
            onClick={prev}
            disabled={start === 0}
          >
            <Icon name='back' size={16} />
          </button>
        )}
        <div className='ef-d-gallery__viewport' {...swipe}>
          <div
            className='ef-d-gallery__track'
            style={trackStyle}
          >
            {all.map((it, i) => (
              <figure key={i} onClick={() => setLightbox(i)}>
                <div className={`ef-d-gallery__img${isVideoUrl(it.src) ? ' is-video' : ''}`}>
                  {isVideoUrl(it.src) ? (
                    <video
                      src={it.src}
                      muted
                      playsInline
                      preload='metadata'
                      onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                      onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                    />
                  ) : (
                    <img src={it.src} alt='' />
                  )}
                </div>
                <figcaption>
                  {(it.cap || '').split('\n').map((line, j) => <span key={j}>{line}</span>)}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        {overflow && (
          <button
            className='ef-d-gallery__nav next'
            aria-label='Next'
            type='button'
            onClick={next}
            disabled={start === maxStart}
          >
            <Icon name='arrow-right' size={16} />
          </button>
        )}
      </div>
      {overflow && (
        <div className='ef-d-gallery__dots'>
          {Array.from({ length: dots }).map((_, i) => (
            <span
              key={i}
              className={i === start ? 'active' : ''}
              onClick={() => setStart(i)}
              role='button'
              tabIndex={0}
            />
          ))}
        </div>
      )}
      {lightbox !== null && (
        <Lightbox
          items={all}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => (i - 1 + all.length) % all.length)}
          onNext={() => setLightbox((i) => (i + 1) % all.length)}
        />
      )}
    </div>
  );
}

function Applications({ d, t, viewAllHref, viewAllLabel }) {
  const apps = arrOr(d && d.expansion, []);
  const VISIBLE = useVisibleCount(3);
  const maxStart = Math.max(0, apps.length - VISIBLE);
  const [start, setStart] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const items = apps.map((a) => ({ src: a.img, cap: a.t }));

  useEffect(() => {
    setStart((s) => Math.min(s, maxStart));
  }, [maxStart]);

  if (apps.length === 0) return null;
  const overflow = apps.length > VISIBLE;
  const dots = maxStart + 1;
  const swipe = useSwipe(
    () => setStart((s) => Math.max(0, s - 1)),
    () => setStart((s) => Math.min(maxStart, s + 1)),
  );
  const trackStyle = {
    gridAutoColumns: `calc((100% - 8px * ${VISIBLE - 1}) / ${VISIBLE})`,
    transform: `translateX(calc(-${start} * (100% / ${VISIBLE} + 8px / ${VISIBLE})))`,
  };

  return (
    <div className='ef-d-col'>
      <div className='ef-d-col__head'>
        <h2 className='ef-d-h'><span className='n'></span> {t.section5.h}</h2>
        {viewAllHref && viewAllLabel && (
          <Link href={viewAllHref}>
            <a className='ef-d-col__more'>
              <span>{viewAllLabel}</span>
              <Icon name='arrow-right' size={14} />
            </a>
          </Link>
        )}
      </div>
      <div className='ef-d-apps'>
        {overflow && (
          <button
            className='ef-d-apps__nav prev'
            aria-label='Previous'
            type='button'
            onClick={() => setStart((s) => Math.max(0, s - 1))}
            disabled={start === 0}
          >
            <Icon name='back' size={16} />
          </button>
        )}
        <div className='ef-d-apps__viewport' {...swipe}>
          <div
            className='ef-d-apps__track'
            style={trackStyle}
          >
            {apps.map((a, i) => (
              <figure key={i} className='ef-d-app' onClick={() => setLightbox(i)}>
                <div className='ef-d-app__img'><img src={a.img} alt={a.t} /></div>
                <figcaption>{a.t}</figcaption>
              </figure>
            ))}
          </div>
        </div>
        {overflow && (
          <button
            className='ef-d-apps__nav next'
            aria-label='Next'
            type='button'
            onClick={() => setStart((s) => Math.min(maxStart, s + 1))}
            disabled={start === maxStart}
          >
            <Icon name='arrow-right' size={16} />
          </button>
        )}
      </div>
      {overflow && (
        <div className='ef-d-apps__dots'>
          {Array.from({ length: dots }).map((_, i) => (
            <span
              key={i}
              className={i === start ? 'active' : ''}
              onClick={() => setStart(i)}
              role='button'
              tabIndex={0}
            />
          ))}
        </div>
      )}
      {lightbox !== null && (
        <Lightbox
          items={items}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() => setLightbox((i) => (i - 1 + items.length) % items.length)}
          onNext={() => setLightbox((i) => (i + 1) % items.length)}
        />
      )}
    </div>
  );
}

function RelatedCases({ current, related: relatedProp, t }) {
  const related = (Array.isArray(relatedProp) ? relatedProp : []).slice(0, 3);
  if (related.length === 0) {
    return (
      <div className='ef-d-col'>
        <h2 className='ef-d-h'><span className='n'></span> {t.section6.h}</h2>
        <div className='ef-d-related'>
          <Link href='/case-studies'>
            <a className='ef-d-related__more'>
              <span>{t.section6.allCta}</span>
              <Icon name='arrow-right' size={16} />
            </a>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className='ef-d-col'>
      <h2 className='ef-d-h'><span className='n'>6.</span> {t.section6.h}</h2>
      <div className='ef-d-related'>
        {related.map((r) => {
          const title = (r.title || '').trim();
          const shortTitle = title ? title.split(' cho ')[0] : '';
          return (
            <Link key={r.slug} href={`/case-studies/${r.slug}`}>
              <a className='ef-d-related__card'>
                <div className='img'><img src={r.image} alt={title} /></div>
                <div className='body'>
                  <h5>{shortTitle}</h5>
                </div>
              </a>
            </Link>
          );
        })}
        <Link href='/case-studies'>
          <a className='ef-d-related__more'>
            <span>{t.section6.moreCta}</span>
            <Icon name='arrow-right' size={16} />
          </a>
        </Link>
      </div>
    </div>
  );
}

function BottomCTA({ t }) {
  return (
    <section className='ef-d-section' style={{ paddingTop: 28, paddingBottom: 96, marginBottom: 64 }}>
      <div className='ef-container'>
        <div className='ef-cta-banner'>
          <div className='shield'>
            <svg width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
              <path d='M9 12l2 2 4-4' />
            </svg>
          </div>
          <div className='copy'>
            <h3>{t.cta.h}</h3>
            <p>{t.cta.desc}</p>
          </div>
          <Link href='/contact'>
            <a
              className='ef-btn ef-btn--lg'
              style={{ background: '#fff', color: 'var(--ef-blue-primary)' }}
            >
              {t.cta.btn}
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                <line x1='5' y1='12' x2='19' y2='12' />
                <polyline points='12 5 19 12 12 19' />
              </svg>
            </a>
          </Link>
        </div>
      </div>
    </section>
  );
}

function CaseStudyDetail({ slug, caseStudy, related, translations }) {
  const router = useRouter();
  const trans = useTrans();
  const t = trans.caseStudies.detail;
  // Prefer API-provided case; fall back to bundled mock for local dev.
  const c = caseStudy || findCaseBySlug(slug);

  // Tell the Header how to switch languages on this page: each supported
  // locale maps to its translated slug, or to `null` (= go to the list)
  // when no published translation exists for that locale.
  //
  // Only register an override when the API actually returned translations —
  // otherwise we'd hijack the language switcher for the bundled mock
  // fallback used in local dev and always redirect to the list.
  const localeHrefMap = useMemo(() => {
    const list = Array.isArray(translations) ? translations : [];
    if (list.length === 0) return null;
    const bySign = {};
    list.forEach((t) => { if (t && t.sign) bySign[t.sign] = t; });
    const map = {};
    SUPPORTED_LOCALES.forEach((loc) => {
      const t = bySign[loc];
      map[loc] = t && t.slug ? `/case-studies/${t.slug}` : null;
    });
    return map;
  }, [translations]);
  useRegisterLocaleHref(localeHrefMap);

  useEffect(() => {
    document.body.setAttribute('data-cs-theme', 'dark');
    return () => {
      document.body.removeAttribute('data-cs-theme');
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }, [slug]);

  if (!c) {
    return (
      <div data-theme='dark' style={{ padding: '180px 0 120px', textAlign: 'center', color: '#fff' }}>
        <p>{t.notFound}</p>
        <Link href='/case-studies'><a className='ef-btn ef-btn--primary'>{t.backToList}</a></Link>
      </div>
    );
  }

  // The CMS persists a 6-key skeleton even when every section is empty, so
  // we can't just check Object.keys.length. Treat the detail as "present"
  // only when at least one meaningful section actually has content.
  const hasDetail = (() => {
    const dd = c.detail;
    if (!dd || typeof dd !== 'object') return false;
    if (dd.overview && (dd.overview.sub || (dd.overview.images || []).length)) return true;
    if (dd.challenge && ((dd.challenge.blocks || []).length || (dd.challenge.body || []).length || (dd.challenge.bullets || []).length || (dd.challenge.images || []).length)) return true;
    if (dd.solution && (dd.solution.intro || (dd.solution.images || []).length)) return true;
    if ((dd.howItWorks || []).length) return true;
    if ((dd.benefits || []).length) return true;
    if (dd.real && ((dd.real.images || []).length || (dd.real.captions || []).length)) return true;
    return false;
  })();
  const d = hasDetail ? c.detail : buildDetail(c);

  return (
    <main className='ef-detail' data-theme='dark'>
      <div className='ef-d-back'>
        <div className='ef-container'>
          <Link href='/case-studies'>
            <a>
              <Icon name='back' size={16} />
              {t.backToList}
            </a>
          </Link>
        </div>
      </div>

      <DetailHero c={c} d={d} t={t} />
      <OverviewChallenge c={c} d={d} t={t} />
      <SolutionModel d={d} t={t} />

      <section className='ef-d-section'>
        <div className='ef-container'>
          <div className='ef-d-pair'>
            <Results d={d} t={t} />
            <RealImages d={d} t={t} />
          </div>
        </div>
      </section>
      {(() => {
        const relatedItems = Array.isArray(related) ? related.filter(Boolean) : [];
        const hasRelated = relatedItems.length > 0;
        const hasApps = arrOr(d && d.expansion, []).length > 0;
        if (!hasApps && !hasRelated) return null;
        return (
          <section className='ef-d-section'>
            <div className='ef-container'>
              {hasApps && hasRelated ? (
                <div className='ef-d-pair'>
                  <Applications d={d} t={t} />
                  <RelatedCases current={c} related={relatedItems} t={t} />
                </div>
              ) : hasApps ? (
                <Applications
                  d={d}
                  t={t}
                  viewAllHref='/case-studies'
                  viewAllLabel={t.section6.allCta}
                />
              ) : (
                <RelatedCases current={c} related={relatedItems} t={t} />
              )}
            </div>
          </section>
        );
      })()}

      <BottomCTA t={t} />
    </main>
  );
}

export default CaseStudyDetail;
